import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Symbol } from "../../../../../../../models/symbol";
import { Status, SymbolType } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import ExceptionLocationCodeModal from "./ExceptionLocationCodeModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const ExceptionLocationCode = ({ permission }: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const {
    symbols,
    page,
    pageSize,
    total,
    hasErrors,
    isLoading,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createSymbol,
    updateSymbol,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedSymbol, setSelectedSymbol] = useState<Symbol>();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const editSymbol = (index: number) => {
    const symbolToUpdate = symbols.find((symbol) => Number(symbol.id) === index);

    if (symbolToUpdate) {
      if (hasErrors !== undefined) setHasErrors(undefined);
      setSelectedSymbol(symbolToUpdate);
      setOpenModal(true);
    }
  };

  const changeStatusSymbol = (index: number) => {
    const symbolToUpdate = symbols.find((symbol) => Number(symbol.id) === index);

    if (symbolToUpdate)
      updateSymbol({
        id: Number(index),
        symbol: {
          ...symbolToUpdate,
          status: symbolToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveSymbolsSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, symbols);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.symbols",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="symbolListTable">
      <ShipeeziNewGrid
        data={symbols}
        columns={[
          { name: "prefix", title: translate("code") },
          {
            name: "name",
            title: translate("name"),
            formatComponent: (value?: string) => value,
          },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (status: string) => badgeStatus(status),
            hideSearch: true,
          },
        ]}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (symbolId) => editSymbol(Number(symbolId))
            : undefined,
          handleChange: (symbolId) => changeStatusSymbol(Number(symbolId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveSymbolsSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setSelectedSymbol(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <ExceptionLocationCodeModal
          open={openModal}
          hasError={hasErrors}
          title={
            selectedSymbol
              ? `${translate("edit")} ${translate("exceptionLocationCode").toLowerCase()}`
              : `${translate("create")} ${translate("exceptionLocationCode").toLowerCase()}`
          }
          modalTitle={translate("exceptionLocationCode")}
          isLoading={isLoading}
          symbolToUpdate={selectedSymbol}
          closeModal={() => setOpenModal(false)}
          submitSymbol={(symbol: Symbol) => {
            if (selectedSymbol && selectedSymbol.id)
              updateSymbol({
                id: selectedSymbol.id,
                symbol: { ...selectedSymbol, ...symbol },
              });
            else
              createSymbol({
                ...symbol,
                property: false,
                type: `${SymbolType.LOCATION}`,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateSymbol({ id: rowId, symbol: { status } as Symbol });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default ExceptionLocationCode;
